// src/pages/Admin/AdminDocumentationDocuments.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Grid,
  Autocomplete,
  TextField,
  Button,
  CircularProgress,
} from "@mui/material";
import { debounce } from "@mui/material/utils";
import { useTranslation } from "react-i18next";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import Documentation from "../Layout2/Documentation/Documentation";
import DataGridSkeletonForDocumentation from "../../components/PageSkeletons/DataGridSkeletonForDocumentation";
import { getMachinesByProvision } from "../../services/analyticsService";
import { getCustomersCusSites } from "../../services/manageCustomerMasterService";
import { getAllCustomerSites } from "../../services/manageSitesMasterService";
import { useAuth } from "../../context/authContext";
import useActorAllocationScope from "../../hooks/useActorAllocationScope";


const AdminDocumentationDocuments = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const {
    scopeLoading,
    actorSiteIds,
    actorCustomerIds,
    actorHasAllSiteVisibility,
  } = useActorAllocationScope();

  // States - RAW DATA + OPTIONS
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [machines, setMachines] = useState([]);
  const [customersOptions, setCustomersOptions] = useState([]);
  const [sitesOptions, setSitesOptions] = useState([]);
  const [machinesOptions, setMachinesOptions] = useState([]);

  const [customerValue, setCustomerValue] = useState(null);
  const [locationValue, setLocationValue] = useState(null);
  const [machineValue, setMachineValue] = useState(null);

  // Debounced search states
  const [customerInput, setCustomerInput] = useState("");
  const [locationInput, setLocationInput] = useState("");
  const [machineInput, setMachineInput] = useState("");

  const [loading, setLoading] = useState(true);

  // Fetch functions - SET RAW DATA
  const fetchCustomers = useCallback(async () => {
    try {
      const response = await getCustomersCusSites();
      if (response.status) {
        const allCustomers = response.data.filter(
          (customer) => customer.customer_name && customer.customer_name !== "EMPTY"
        );
        const filteredCustomers = actorHasAllSiteVisibility ? allCustomers : allCustomers.filter((c) =>
          actorCustomerIds.includes(Number(c.cus_id))
        );
        const options = filteredCustomers.map((cus) => ({
          value: cus.cus_id,
          label: `${cus.customer_name} - ${cus.customer_number}`,
        }));
        
        setCustomersOptions(options);
        setCustomers(filteredCustomers);
      }
    } catch (err) {
      console.error("Error fetching customers:", err);
    }
  }, [actorCustomerIds, actorHasAllSiteVisibility]);

  const fetchSites = useCallback(async () => {
    try {
      const response = await getAllCustomerSites();
      if (response.status) {
        const allSites = response.data.filter(
          (site) => site.customer_site_name && site.customer_site_name !== "EMPTY"
        );
        const filteredSites = actorHasAllSiteVisibility ? allSites : allSites.filter((s) =>
          actorSiteIds.includes(Number(s.site_id))
        );
        const options = filteredSites.map((site) => ({
          value: site.site_id,
          label: `${site.customer_site_name} - ${site.customer_site_number}`,
        }));
        
        setSitesOptions(options);
        setSites(filteredSites);
      }
    } catch (err) {
      console.error("Error fetching sites:", err);
    }
  }, [actorHasAllSiteVisibility, actorSiteIds]);

  const fetchMachines = useCallback(async () => {
    try {
      const response = await getMachinesByProvision();
      if (response.status) {
        const allMachines = response.data;
        const filteredMachines = actorHasAllSiteVisibility ? allMachines : allMachines.filter((m) =>
          actorSiteIds.includes(Number(m.site_id)) ||
          actorCustomerIds.includes(Number(m.cus_id))
        );
        const options = filteredMachines.map((machine) => ({
          value: machine.machine_id,
          label: `${machine.machine_name} - ${machine.machine_number}`,
        }));
        
        setMachinesOptions(options);
        setMachines(filteredMachines);
      }
    } catch (error) {
      console.error("Error fetching machines:", error);
    }
  }, [actorCustomerIds, actorHasAllSiteVisibility, actorSiteIds]);

  // SINGLE INIT
  useEffect(() => {
    if (!user || scopeLoading) return;
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchCustomers(), fetchSites(), fetchMachines()]);
      setLoading(false);
    };
    init();
  }, [fetchCustomers, fetchMachines, fetchSites, scopeLoading, user]);

  // DEBOUNCED INPUT HANDLERS (300ms delay)
  const debouncedCustomerInput = useCallback(
    debounce((value) => {
      setCustomerInput(value);
    }, 300),
    []
  );

  const debouncedLocationInput = useCallback(
    debounce((value) => {
      setLocationInput(value);
    }, 300),
    []
  );

  const debouncedMachineInput = useCallback(
    debounce((value) => {
      setMachineInput(value);
    }, 300),
    []
  );

  const ALL_CUSTOMERS_OPTION = useMemo(() => ({
    value: t('ADMIN_FILTERS.ALL_CUSTOMERS'),
    label: t('ADMIN_FILTERS.ALL_CUSTOMERS')
  }), [t]);

  const ALL_LOCATIONS_OPTION = useMemo(() => ({
    value: t('ADMIN_FILTERS.ALL_LOCATIONS'),
    label: t('ADMIN_FILTERS.ALL_LOCATIONS')
  }), [t]);

  const ALL_MACHINES_OPTION = useMemo(() => ({
    value: t('ADMIN_FILTERS.ALL_MACHINES'),
    label: t('ADMIN_FILTERS.ALL_MACHINES')
  }), [t]);

  // ─── FIX #1: Helper to detect "All *" sentinel values ───────────────────────
  // These string sentinels must never be forwarded to the backend as bigint IDs.
  const isAllCustomersSelected = useMemo(
    () => customerValue?.value === ALL_CUSTOMERS_OPTION.value,
    [customerValue, ALL_CUSTOMERS_OPTION.value]
  );
  const isAllLocationsSelected = useMemo(
    () => locationValue?.value === ALL_LOCATIONS_OPTION.value,
    [locationValue, ALL_LOCATIONS_OPTION.value]
  );
  const isAllMachinesSelected = useMemo(
    () => machineValue?.value === ALL_MACHINES_OPTION.value,
    [machineValue, ALL_MACHINES_OPTION.value]
  );
  // ────────────────────────────────────────────────────────────────────────────

  const findCustomerOption = useCallback(
    (cusId) => customersOptions.find((customer) => String(customer?.value) === String(cusId)) || null,
    [customersOptions]
  );

  const findSiteOption = useCallback(
    (siteId) => sitesOptions.find((site) => String(site?.value) === String(siteId)) || null,
    [sitesOptions]
  );

  const findMachine = useCallback(
    (machineId) => machines.find((machine) => String(machine?.machine_id) === String(machineId)) || null,
    [machines]
  );

  const findSite = useCallback(
    (siteId) => sites.find((site) => String(site?.site_id) === String(siteId)) || null,
    [sites]
  );

  // PERFECT cascading filters with debounced search
  const filteredSitesOptions = useMemo(() => {
    let filtered = sitesOptions;

    if (customerValue?.value && !isAllCustomersSelected) {
      filtered = filtered.filter(option =>
        String(sites.find(s => s.site_id === option.value)?.cus_id) === String(customerValue.value)
      );
    }

    if (locationInput.trim() && !isAllLocationsSelected) {
      filtered = filtered.filter(option =>
        option.label.toLowerCase().includes(locationInput.toLowerCase())
      );
    }

    return filtered;
  }, [customerValue?.value, isAllCustomersSelected, sitesOptions, sites, locationInput, isAllLocationsSelected]);

  const filteredMachinesOptions = useMemo(() => {
    let filtered = machines;

    if (locationValue?.value && !isAllLocationsSelected) {
      filtered = filtered.filter(m => String(m.site_id) === String(locationValue.value));
    } else if (customerValue?.value && !isAllCustomersSelected) {
      filtered = filtered.filter(m => String(m.cus_id) === String(customerValue.value));
    }

    const options = filtered.map(machine => ({
      value: machine.machine_id,
      label: `${machine.machine_name} - ${machine.machine_number}`,
    }));

    if (machineInput.trim()) {
      return options.filter(option =>
        option.label.toLowerCase().includes(machineInput.toLowerCase())
      );
    }

    return options;
  }, [locationValue?.value, isAllLocationsSelected, customerValue?.value, isAllCustomersSelected, machines, machineInput]);

  const filteredCustomersOptions = useMemo(() => {
    if (!customerInput.trim()) return customersOptions;
    return customersOptions.filter(option =>
      option.label.toLowerCase().includes(customerInput.toLowerCase())
    );
  }, [customersOptions, customerInput]);

  // CLEAR ALL FILTERS HANDLER
  const handleClearFilters = () => {
    
    setCustomerValue(null);
    setLocationValue(null);
    setMachineValue(null);
    setCustomerInput("");
    setLocationInput("");
    setMachineInput("");
  };

  const adminLocationSiteId = (
    !locationValue?.value ||
    isAllLocationsSelected ||
    locationValue.value === t('ADMIN_FILTERS.ALL_LOCATIONS')
  )
    ? null
    : locationValue.value;

  const adminMachine = (
    !machineValue?.value ||
    isAllMachinesSelected ||
    machineValue.value === t('ADMIN_FILTERS.ALL_MACHINES')
  )
    ? "all"
    : machineValue.value;

  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    // force a remount once after first paint
    const id = requestAnimationFrame(() => {
      setRefreshKey(prev => prev + 1);
    });
    return () => cancelAnimationFrame(id);
  }, []);

  // Check if any filter is active
  const hasActiveFilters = customerValue?.value || locationValue?.value || machineValue?.value;

  const customerSiteIds = useMemo(() => {
    if (!customerValue?.value || isAllCustomersSelected) return [];
    return sites
      .filter(s => String(s.cus_id) === String(customerValue.value))
      .map(s => s.site_id);
  }, [customerValue?.value, isAllCustomersSelected, sites]);

  const searchBysiteIds = useMemo(() => {
    if (locationValue?.value && !isAllLocationsSelected) return false;
    if (customerValue?.value && !isAllCustomersSelected) return true;
    return false;
  }, [customerValue?.value, isAllCustomersSelected, locationValue?.value, isAllLocationsSelected]);

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          mb: 2,
          mt: 1,
          display: "flex",
          flexDirection: "column",
          rowGap: '5px',
          background: '#eee',
          p: 2,
          borderRadius: '10px'
        }}
      >
        <Typography fontWeight={600} mb={0.5}>{t('ADMIN_FILTERS.TITLE')}</Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          {t('ADMIN_FILTERS.DESCRIPTION')}
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Autocomplete
              size="small"
              options={[ALL_CUSTOMERS_OPTION, ...filteredCustomersOptions]}
              value={customerValue}
              onChange={(e, newVal) => {
                
                if (!newVal || newVal.value === null || newVal.value === ALL_CUSTOMERS_OPTION.value) {
                  setCustomerValue(null);
                  setLocationValue(null);
                  setMachineValue(null);
                } else {
                  setCustomerValue(newVal);
                  const selectedSite = locationValue?.value ? findSite(locationValue.value) : null;
                  if (selectedSite && String(selectedSite.cus_id) !== String(newVal.value)) {
                    setLocationValue(null);
                  }
                  const selectedMachine = machineValue?.value ? findMachine(machineValue.value) : null;
                  if (selectedMachine && String(selectedMachine.cus_id) !== String(newVal.value)) {
                    setMachineValue(null);
                  }
                }
              }}
              onInputChange={(e, newInputValue) => {
                debouncedCustomerInput(newInputValue);
              }}
              isOptionEqualToValue={(option, value) => option?.value === value?.value}
              getOptionLabel={(option) => option?.label || ""}
              renderInput={(params) => (
                <TextField {...params} label={t('ADMIN_FILTERS.CUSTOMER')} placeholder={t('ADMIN_FILTERS.SEARCH_CUSTOMERS')} />
              )}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Autocomplete
              size="small"
              options={[ALL_LOCATIONS_OPTION, ...filteredSitesOptions]}
              value={locationValue}
              onChange={(e, newVal) => {
                if (!newVal || newVal.value === null || newVal.value === ALL_LOCATIONS_OPTION.value) {
                  setLocationValue(null);
                  setMachineValue(null);
                } else {
                  setLocationValue(newVal);
                  const selectedSite = findSite(newVal.value);
                  const relatedCustomer = selectedSite ? findCustomerOption(selectedSite.cus_id) : null;
                  if (relatedCustomer) {
                    setCustomerValue(relatedCustomer);
                  }
                  const selectedMachine = machineValue?.value ? findMachine(machineValue.value) : null;
                  if (selectedMachine && String(selectedMachine.site_id) !== String(newVal.value)) {
                    setMachineValue(null);
                  }
                }
              }}
              onInputChange={(e, newInputValue) => {
                debouncedLocationInput(newInputValue);
              }}
              isOptionEqualToValue={(option, value) => option?.value === value?.value}
              getOptionLabel={(option) => option?.label || ""}
              renderInput={(params) => (
                <TextField {...params} label={t('ADMIN_FILTERS.LOCATION')} placeholder={t('ADMIN_FILTERS.SEARCH_LOCATIONS')} />
              )}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Autocomplete
              size="small"
              options={[ALL_MACHINES_OPTION, ...filteredMachinesOptions]}
              value={machineValue}
              onChange={(e, newVal) => {
                
                if (!newVal || newVal.value === null || newVal.value === ALL_MACHINES_OPTION.value) {
                  setMachineValue(null);
                } else {
                  setMachineValue(newVal);
                  const selectedMachine = findMachine(newVal.value);
                  if (selectedMachine) {
                    const relatedLocation = findSiteOption(selectedMachine.site_id);
                    const relatedCustomer = findCustomerOption(selectedMachine.cus_id);
                    if (relatedLocation) {
                      setLocationValue(relatedLocation);
                    }
                    if (relatedCustomer) {
                      setCustomerValue(relatedCustomer);
                    }
                  }
                }
              }}
              onInputChange={(e, newInputValue) => {
                debouncedMachineInput(newInputValue);
              }}
              isOptionEqualToValue={(option, value) => option?.value === value?.value}
              getOptionLabel={(option) => option?.label || ""}
              renderInput={(params) => (
                <TextField {...params} label={t('ADMIN_FILTERS.MACHINE')} placeholder={t('ADMIN_FILTERS.SEARCH_MACHINES')} />
              )}
            />
          </Grid>
        </Grid>
      </Box>

      <Box sx={{
        overflow: 'auto',
        width: '100%'
      }}>
        <Box sx={{
          width: '100%',
          overflow: 'visible',
          position: 'relative',
        }}>
          {loading ? (
            <DataGridSkeletonForDocumentation
              showSidebar={false}
              showTabs={true}
              tabCount={7}
              showSearch={true}
              rows={10}
            />
          ) : (
            <Documentation
              key={refreshKey}
              hideSidebar={true}
              adminLocation={adminLocationSiteId}
              adminMachine={adminMachine}
              isAdminView={true}
              filterdSites={filteredSitesOptions}
              searchBysiteIds={searchBysiteIds}
              customerSiteIds={customerSiteIds}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default AdminDocumentationDocuments;
